import React, { useState } from "react";
import { Sparkles, Check, X, ExternalLink } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * B10d — one "Suggest" action per organization field. Calls the single
 * field-level endpoint POST /enrichment/suggest-field. Nothing is saved until
 * the user clicks Accept (which stores source "ai_suggestion"; manual wins).
 * Department-head personal data must NOT render this component.
 */
export const SuggestField = ({ orgId, field, onAccept }) => {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sug, setSug] = useState(null);   // {value, source, source_link, label, message}
  const suggest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/enrichment/suggest-field", { org_id: orgId, field, action: "suggest" });
      setSug(data);
    } catch (e) {
      setSug({ value: null, message: "No reliable source found" });
    } finally { setBusy(false); }
  };
  const accept = async () => {
    try {
      await api.post("/enrichment/suggest-field", { org_id: orgId, field, action: "accept", value: sug.value });
      onAccept && onAccept(sug.value, sug.source || "ai_suggestion");
    } catch (e) { /* noop */ }
    setSug(null);
  };
  return (
    <span className="inline-flex items-center gap-1">
      <button type="button" onClick={suggest} disabled={busy}
        className="text-[10px] text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-1.5 py-0.5 inline-flex items-center gap-1"
        data-testid={`suggest-${field}`}>
        <Sparkles className="w-3 h-3" /> {busy ? "…" : "Suggest"}
      </button>
      {sug && (
        <span className="text-[11px] bg-white border border-slate-200 rounded px-2 py-1 inline-flex items-center gap-2" data-testid={`suggestion-${field}`}>
          {sug.value != null && sug.value !== "" ? (
            <>
              <span className="font-medium text-slate-700">{Array.isArray(sug.value) ? sug.value.join(", ") : String(sug.value)}</span>
              {sug.label && <span className="text-slate-400">({sug.label})</span>}
              {sug.source_link && (
                <a href={sug.source_link} target="_blank" rel="noreferrer" className="text-sky-600 inline-flex items-center" data-testid={`suggestion-link-${field}`}>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <button type="button" onClick={accept} className="text-emerald-600" data-testid={`suggestion-accept-${field}`}><Check className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => setSug(null)} className="text-red-500" data-testid={`suggestion-reject-${field}`}><X className="w-3.5 h-3.5" /></button>
            </>
          ) : (
            <>
              <span className="text-slate-500" data-testid={`suggestion-none-${field}`}>{sug.message || "No reliable source found"}</span>
              <button type="button" onClick={() => setSug(null)} className="text-red-500"><X className="w-3.5 h-3.5" /></button>
            </>
          )}
        </span>
      )}
    </span>
  );
};

export default SuggestField;
